import { ROUND_CLOCKS, roundKind } from '../rounds';
import { backtestStrategy, type BacktestRound } from './backtest';
import { comboStrategy, type ComboParams } from './combo';
import { presetParams, presetStrategyFor, type InvexaPreset, type PresetModality } from './presets';

/** The owner's target for the Automação Invexa (2026-10-26): at least one cycle a minute. */
export const CYCLE_GOAL_PER_MINUTE = 1;

export interface CycleCount {
  /** Cycles finished (first entry + its gales): won + lost. One still running at the end isn't counted. */
  cycles: number;
  /** Cycles won (at the first entry or a gale): assertividade = won / cycles, as the strategies page reads it. */
  won: number;
  /** Cycles that lost the last gale. */
  lost: number;
  /**
   * The period cut in two halves of equal open market: cycles and wins of each, to tell whether the
   * assertividade went up or down (later half vs earlier half). Null with too few cycles in a half.
   */
  trend: { before: { cycles: number; won: number }; after: { cycles: number; won: number } } | null;
}

/** Fewest cycles in each half for the trend to mean anything. */
const TREND_MIN_CYCLES = 3;

export interface PresetFrequency {
  /** Minutes of open market the rounds cover (rounds × the game's clock): market pauses don't dilute the rate. */
  minutes: number;
  rounds: number;
  /** Each strategy alone, in list order. */
  each: (CycleCount & { key: string })[];
  /** The Automação Invexa: the whole list together (one cycle at a time; the one above takes a shared round). */
  auto: CycleCount | null;
}

/**
 * How often each strategy of a game's list, and the Automação Invexa running them all, start a cycle on
 * real rounds — replayed exactly as the robot runs them (loss virtual, gale side, pauses). Each one at its
 * own gales; the Automação at the most gales on the list. Past rounds never promise future ones.
 */
export function presetFrequency(presets: readonly InvexaPreset[], modality: PresetModality, rounds: readonly BacktestRound[]): PresetFrequency {
  const minutes = (rounds.length * ROUND_CLOCKS[roundKind(modality)].cycleSeconds) / 60;
  const list = presets.filter((p) => p.modality === modality && !p.retired);
  // Middle of the period by open market (the round in the middle), so both halves are equally long.
  const mid = rounds[Math.floor(rounds.length / 2)]?.endAt ?? 0;
  const run = (strategy: unknown, params: unknown, gales: number): CycleCount => {
    const r = backtestStrategy({
      strategy: strategy as never,
      params,
      modality,
      rounds,
      baseStake: 1,
      progression: { enabled: gales > 0, maxLevels: gales, multiplier: '2.15', onMaxLevel: 'RESET' },
      payoutRate: 0.88,
    });
    // Cycles in time, counted exactly as the backtest counts them: a first entry that came back starts none.
    const halves = { before: { cycles: 0, won: 0 }, after: { cycles: 0, won: 0 } };
    let half: (typeof halves)['before'] | null = null;
    let open = false;
    for (const e of r.entriesLog) {
      if (e.level === 0 && e.outcome !== 'NEUTRAL') {
        half = e.at < mid ? halves.before : halves.after;
        half.cycles++;
        open = true;
      }
      if (e.outcome === 'WIN' && half) half.won++;
      if (e.outcome === 'WIN' || (e.outcome === 'LOSS' && e.level >= gales)) open = false;
    }
    // A cycle still running when the period ends (live data almost always has one) is neither won nor lost
    // yet: left out, so a strategy that never lost never reads 459/460.
    if (open && half) half.cycles--;
    const trend = halves.before.cycles >= TREND_MIN_CYCLES && halves.after.cycles >= TREND_MIN_CYCLES ? halves : null;
    return { cycles: r.chains - (open ? 1 : 0), won: r.chainWins, lost: r.chainLosses, trend };
  };
  // Statistics of the strategy over time: its own stops (end the session after N cycles) don't cut the count.
  const noStops = (p: InvexaPreset): InvexaPreset => ({ ...p, def: { ...p.def, stops: { lossStreak: 0, wins: 0 } } });
  const each = list.map((p) => ({ key: p.key, ...run(presetStrategyFor(p.key), presetParams(noStops(p)), p.gales) }));
  // The Automação Invexa with copied robots runs as this combination (see autoPatterns).
  const combo = { v: 1, robots: list.map((p) => ({ id: p.key, name: p.name, def: noStops(p).def })) } as ComboParams;
  const auto = list.length ? run(comboStrategy, combo, Math.max(...list.map((p) => p.gales))) : null;
  return { minutes, rounds: rounds.length, each, auto };
}

/** "1 ciclo a cada 5,1 min" / "3,2 ciclos por minuto" — the rate as the admin reads it. */
export function cycleRateText(cycles: number, minutes: number): string {
  if (!cycles || !minutes) return 'nenhum ciclo no período';
  const perMin = cycles / minutes;
  const n = (v: number) => v.toLocaleString('pt-BR', { maximumFractionDigits: 1, minimumFractionDigits: v < 10 ? 1 : 0 });
  return perMin >= 1 ? `${n(perMin)} ciclos por minuto` : `1 ciclo a cada ${n(1 / perMin)} min`;
}

/** "460/462 · 99,6%": cycles won of cycles, and the assertividade. */
export function accuracyText(won: number, cycles: number): string {
  if (!cycles) return 'sem ciclos';
  return `${won.toLocaleString('pt-BR')}/${cycles.toLocaleString('pt-BR')} · ${percentText(won / cycles)}`;
}

/** 1 → "100%", 0.99613 → "99,6%" (never rounds a miss up to 100%). */
export function percentText(rate: number): string {
  const pct = Math.floor(rate * 1000) / 10;
  return `${pct.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;
}

/** Later half's assertividade minus the earlier half's, in percentage points (null = too few cycles). */
export function trendPoints(c: CycleCount): number | null {
  if (!c.trend) return null;
  const { before, after } = c.trend;
  return (after.won / after.cycles - before.won / before.cycles) * 100;
}

